import { reportService } from '../service/instances';
import { InterventionReportModel } from '../models/interventionReportModel';
import { downloadBlobAsFile } from './downloadBlobAsFile';
import { formatTodayDate } from './helper';
import { BulkDownloadResult } from './helper.type';

export const downloadReportsAsZip = async (
  reports: InterventionReportModel[],
): Promise<BulkDownloadResult> => {
  const downloadableReports: InterventionReportModel[] = reports.filter(
    (report: InterventionReportModel) => report.originalReportId,
  );

  if (downloadableReports.length === 0) {
    throw new Error('No reports with valid IDs to download');
  }

  const results: PromiseSettledResult<{
    reportRefNo: string;
    pdfBase64: string;
  }>[] = await Promise.allSettled(
    downloadableReports.map(async (report: InterventionReportModel) => {
      const pdfBase64: string = await reportService.downloadInterventionReport(
        report.originalReportId!,
      );
      if (!pdfBase64) {
        throw new Error('No PDF data returned');
      }
      return { reportRefNo: report.reportRefNo, pdfBase64 };
    }),
  );

  const successfulPdfs: { reportRefNo: string; pdfBase64: string }[] = [];
  const failedReportRefNos: string[] = [];

  results.forEach(
    (
      result: PromiseSettledResult<{ reportRefNo: string; pdfBase64: string }>,
      index: number,
    ) => {
      if (result.status === 'fulfilled') {
        successfulPdfs.push(result.value);
      } else {
        failedReportRefNos.push(downloadableReports[index].reportRefNo);
      }
    },
  );

  if (successfulPdfs.length === 0) {
    throw new Error(
      `All ${downloadableReports.length} reports failed to download`,
    );
  }

  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();

  const usedFilenames: Set<string> = new Set();
  let duplicateCounter: number = 0;

  successfulPdfs.forEach((pdf: { reportRefNo: string; pdfBase64: string }) => {
    let filename: string = `Intervention_Report_${pdf.reportRefNo}.pdf`;
    if (usedFilenames.has(filename)) {
      duplicateCounter += 1;
      filename = `Intervention_Report_${pdf.reportRefNo}_${duplicateCounter}.pdf`;
    }
    usedFilenames.add(filename);
    zip.file(filename, pdf.pdfBase64, { base64: true });
  });

  const zipBlob: Blob = await zip.generateAsync({ type: 'blob' });
  const zipFilename: string = `Intervention_Reports_${formatTodayDate()}.zip`;
  downloadBlobAsFile(zipBlob, zipFilename);

  return {
    successCount: successfulPdfs.length,
    failedReportRefNos,
  };
};
